import React, { useMemo } from "react";
import { C, font } from "../lib/styles.js";
import { APP_REGISTRY } from "../lib/appRegistry.js";
import { AppIcon } from "./AppIcons.jsx";
import { LogoMark } from "./Logo.jsx";
import { IdleSetting } from "./IdleSignOut.jsx";
import NeedsYou from "./NeedsYou.jsx";

/* Icon background + stroke color mapping — matches mockup CSS classes */
const ICON_COLORS = {
  text: { bg: "rgba(26,26,26,0.08)", stroke: C.text },
  blue: { bg: "rgba(59,130,246,0.1)", stroke: C.blue },
  green: { bg: "rgba(16,185,129,0.1)", stroke: C.green },
  purple: { bg: "rgba(139,92,246,0.1)", stroke: C.purple },
  amber: { bg: "rgba(245,158,11,0.1)", stroke: C.amber },
  teal: { bg: "rgba(14,148,136,0.1)", stroke: C.teal },
  stone: { bg: "rgba(120,113,108,0.1)", stroke: C.stone },
  indigo: { bg: "rgba(99,102,241,0.1)", stroke: C.indigo },
  danger: { bg: "rgba(239,68,68,0.1)", stroke: C.danger },
  brown: { bg: "rgba(139,107,74,0.1)", stroke: C.brown },
};

const BADGE_STYLES = {
  count: { bg: "rgba(59,130,246,0.1)", color: C.blue },
  feedback: { bg: "rgba(245,158,11,0.1)", color: C.amber },
  live: { bg: "rgba(16,185,129,0.1)", color: C.green },
  dev: { bg: "rgba(245,158,11,0.1)", color: C.amber },
  future: { bg: "rgba(99,102,241,0.1)", color: C.indigo },
};

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export default function Homepage({ user, onOpenApp, badges, onSignOut, onFeedback, idleMinutes, onIdleChange }) {
  const visibleApps = useMemo(() => {
    const userApps = user.apps || [];
    return APP_REGISTRY.filter((app) => userApps.includes(app.id));
  }, [user.apps]);

  const firstName = (user.name || user.email || "").split(" ")[0] || "there";
  const initials = getInitials(user.name || user.email || "");

  return (
    <div className="home-screen" style={styles.screen}>
      {/* Header */}
      <div className="home-header" style={styles.header}>
        <div style={styles.logo}>
          <LogoMark size={36} />
          <span style={styles.logoText}>NYC Suite</span>
        </div>
        <p style={styles.greeting}>
          {getGreeting()}, <strong style={{ fontWeight: 400, color: C.text }}>{firstName}</strong>
        </p>
      </div>

      {/* Needs you: anything left for a person, across every app (admins) */}
      <NeedsYou user={user} onOpenApp={onOpenApp} />

      {/* App Grid */}
      <div className="app-grid" style={styles.grid}>
        {visibleApps.map((app) => (
          <AppCard
            key={app.id}
            app={app}
            badge={badges?.[app.id] || app.badge}
            onOpen={() => onOpenApp(app.id)}
          />
        ))}
      </div>

      {/* Footer. Sign out sits here in plain sight: the Suite is used on
          shared phones and computers (docs/ops/password-signin.md). */}
      <div className="home-footer" style={styles.footer}>
        <div style={styles.footerAvatar}>{initials}</div>
        <span style={styles.footerName}>Signed in as <strong style={{ fontWeight: 500, color: C.text }}>{user.name || user.email}</strong></span>
        <span style={styles.footerRole}>{user.role || "User"}</span>
        {/* Phones only (responsive.css): the desktop has the edge tab. */}
        {onFeedback && (
          <button type="button" className="home-feedback-btn" style={styles.signOut} onClick={onFeedback}>
            Feedback
          </button>
        )}
        {onSignOut && (
          <button type="button" className="home-signout" style={styles.signOut} onClick={onSignOut}>
            Sign out
          </button>
        )}
      </div>
      {/* Admins only: this device's idle sign-out (Off unless set). */}
      {onIdleChange && <IdleSetting minutes={idleMinutes ?? 0} onChange={onIdleChange} />}
    </div>
  );
}

function AppCard({ app, badge, onOpen }) {
  const iconColor = ICON_COLORS[app.color] || ICON_COLORS.text;
  const isWide = app.wide;

  return (
    <a
      className={`app-card${isWide ? " card-wide" : ""}`}
      onClick={onOpen}
      style={{
        ...styles.card,
        ...(isWide ? styles.cardWide : {}),
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#FAFAF8";
        e.currentTarget.style.borderColor = "#D0CDC8";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = C.surface;
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div className="icon-box" style={{ ...styles.iconBox, background: iconColor.bg }}>
        <AppIcon appId={app.id} style={{ color: iconColor.stroke }} />
      </div>

      {badge && !isWide && (
        <span className="card-badge" style={{
          ...styles.badge,
          background: BADGE_STYLES[badge.variant]?.bg,
          color: BADGE_STYLES[badge.variant]?.color,
        }}>
          {badge.text}
        </span>
      )}

      <div className="app-card-content" style={isWide ? { flex: 1 } : {}}>
        <div className="card-name" style={styles.cardName}>{app.name}</div>
        <div className="card-desc" style={styles.cardDesc}>{app.description}</div>
      </div>

      {badge && isWide && (
        <span style={{
          ...styles.badgeStatic,
          background: BADGE_STYLES[badge.variant]?.bg,
          color: BADGE_STYLES[badge.variant]?.color,
        }}>
          {badge.text}
        </span>
      )}
    </a>
  );
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.split(/[\s@]+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/* Styles — matches mockup-homepage.html exactly */

const styles = {
  screen: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "48px 24px 64px",
    animation: "fadeUp 0.3s ease",
  },
  header: {
    textAlign: "center",
    marginBottom: 48,
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginBottom: 16,
  },
  logoText: {
    fontSize: 20,
    fontWeight: 400,
    letterSpacing: "-0.01em",
    color: C.text,
  },
  greeting: {
    fontSize: 14,
    fontWeight: 300,
    color: C.muted,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    maxWidth: 720,
    width: "100%",
  },
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "24px 20px",
    textDecoration: "none",
    color: "inherit",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    position: "relative",
    overflow: "hidden",
    transition: "all 0.15s ease",
  },
  cardWide: {
    gridColumn: "span 2",
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    padding: "20px 24px",
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardName: {
    fontSize: 14,
    fontWeight: 400,
    color: C.text,
  },
  cardDesc: {
    fontSize: 12,
    fontWeight: 300,
    color: C.muted,
    lineHeight: 1.5,
  },
  badge: {
    position: "absolute",
    top: 12,
    right: 12,
    fontSize: 10,
    fontWeight: 500,
    padding: "2px 8px",
    borderRadius: 4,
    letterSpacing: "0.05em",
  },
  badgeStatic: {
    fontSize: 10,
    fontWeight: 500,
    padding: "2px 8px",
    borderRadius: 4,
    letterSpacing: "0.05em",
  },
  footer: {
    marginTop: "auto",
    paddingTop: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  signOut: {
    minHeight: 44,
    padding: "0 16px",
    marginLeft: 8,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    fontSize: 14,
    fontFamily: font,
    cursor: "pointer",
  },
  footerAvatar: {
    width: 28,
    height: 28,
    borderRadius: 9999,
    background: C.surfaceAlt,
    border: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 400,
    color: C.muted,
  },
  footerName: {
    fontSize: 12,
    fontWeight: 300,
    color: C.muted,
  },
  footerRole: {
    fontSize: 10,
    fontWeight: 400,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    padding: "2px 8px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
  },
};
